import { createApi } from "@reduxjs/toolkit/query/react"

import { ApiTypes } from "types"

import { baseQueryWithAuth } from "utils/api"
import { prepareQueryParams } from "utils/prepareQueryParams"

type EnterpriseDashboardWidgetQuery = {
  period: number
  contractorId?: number
}

type EnterpriseDashboardContractorQuery = {
  contractorId?: number
}

export const widgetsApi = createApi({
  reducerPath: "widgets",
  tagTypes: ["widgets"],
  baseQuery: baseQueryWithAuth,
  endpoints: builder => ({
    getInventory: builder.query<{ data: ApiTypes.Res.InventoryWidget }, void>({
      query: () => ({
        url: `/dashboard/widgets/inventory`,
        method: "GET",
      }),
    }),
    getMonitoring: builder.query<{ data: ApiTypes.Res.MonitoringWidget }, void>({
      query: () => ({
        url: `/dashboard/widgets/monitoring`,
        method: "GET",
      }),
    }),
    getStatus: builder.query<{ data: ApiTypes.Res.StatusWidget }, void>({
      query: () => ({
        url: `/dashboard/widgets/status`,
        method: "GET",
      }),
    }),
    getPerftestResults: builder.query<
      { data: ApiTypes.Res.PerftestResultsWidget },
      { date_start?: string; date_end?: string } | void
    >({
      query: params => ({
        url: `/dashboard/widgets/perftest-results`,
        method: "GET",
        ...(params && Object.keys(params).length > 0 && { params }),
      }),
    }),
    getPerftestStatuses: builder.query<{ data: ApiTypes.Res.PerftestStatusesWidget }, void>({
      query: () => ({
        url: `/dashboard/widgets/perftest-statuses`,
        method: "GET",
      }),
    }),
    getAlertTypes: builder.query<
      { data: ApiTypes.Res.AlertTypesWidget },
      { date_start?: string; date_end?: string } | void
    >({
      query: params => ({
        url: `/dashboard/widgets/alert-types`,
        method: "GET",
        params: prepareQueryParams(params),
      }),
    }),
    getAlertStatuses: builder.query<
      { data: ApiTypes.Res.AlertStatusesWidget },
      { date_start?: string; date_end?: string } | void
    >({
      query: params => ({
        url: `/dashboard/widgets/alert-statuses`,
        method: "GET",
        params: prepareQueryParams(params),
      }),
    }),
    getOrderProducts: builder.query<
      { data: ApiTypes.Res.OrderProductsWidget },
      { date_start?: string; date_end?: string } | void
    >({
      query: params => ({
        url: `/dashboard/widgets/order-products`,
        method: "GET",
        params: prepareQueryParams(params),
      }),
    }),
    getOrderStatuses: builder.query<{ data: ApiTypes.Res.ContractorOrderStatusesWidget }, void>({
      query: () => ({
        url: `/dashboard/widgets/order-statuses`,
        method: "GET",
      }),
    }),
    getInstalledDevices: builder.query<
      { data: ApiTypes.Res.InstalledDevicesWidget },
      { date_start?: string; date_end?: string } | void
    >({
      query: params => ({
        url: `/dashboard/widgets/installed-devices`,
        method: "GET",
        params: params ? prepareQueryParams(params) : undefined,
      }),
    }),
    getTotalStats: builder.query<{ data: ApiTypes.Res.TotalStatsWidget }, void>({
      query: () => ({
        url: `/dashboard/widgets/total-stats`,
        method: "GET",
      }),
      keepUnusedDataFor: 0,
    }),
    getCustomersAppsTrend: builder.query<{ data: ApiTypes.Res.CustomersAppsTrendWidget }, { period: number }>({
      query: params => ({
        url: `/dashboard/widgets/customers-apps-trend`,
        method: "GET",
        params: prepareQueryParams(params),
      }),
      keepUnusedDataFor: 3600,
    }),
    getInstallationDevicesTrend: builder.query<
      { data: ApiTypes.Res.InstallationDevicesTrendWidget },
      { period: number }
    >({
      query: params => ({
        url: `/dashboard/widgets/installation-devices-trend`,
        method: "GET",
        params: prepareQueryParams(params),
      }),
      keepUnusedDataFor: 3600,
    }),
    getSystemHealthTrend: builder.query<{ data: ApiTypes.Res.SystemHealthTrendWidget }, { period: number }>({
      query: params => ({
        url: `/dashboard/widgets/system-health-trend`,
        method: "GET",
        params: prepareQueryParams(params),
      }),
      keepUnusedDataFor: 3600,
    }),
    getEnterpriseDashboardTotalStats: builder.query<{ data: ApiTypes.Res.EnterpriseDashboardTotalsWidget }, void>({
      query: () => ({
        url: `/enterprises/dashboard/widgets/total-stats`,
        method: "GET",
      }),
    }),
    getEnterpriseDashboardThermostatTrend: builder.query<
      { data: ApiTypes.Res.InstallationDevicesTrendWidget },
      EnterpriseDashboardWidgetQuery
    >({
      query: params => ({
        url: `/enterprises/dashboard/widgets/trends/thermostats`,
        method: "GET",
        params: prepareQueryParams(params),
      }),
    }),
    getEnterpriseDashboardThermostatCharts: builder.query<
      { data: ApiTypes.Res.EnterpriseDashboardThermostatChartsWidget },
      EnterpriseDashboardWidgetQuery
    >({
      query: params => ({
        url: `/enterprises/dashboard/widgets/charts/thermostats`,
        method: "GET",
        params: prepareQueryParams(params),
      }),
    }),
    getEnterpriseDashboardOrderStatuses: builder.query<
      { data: ApiTypes.Res.EnterpriseDashboardOrderStatusesWidget },
      EnterpriseDashboardContractorQuery | void
    >({
      query: params => ({
        url: `/enterprises/dashboard/widgets/orders/statuses`,
        method: "GET",
        params: prepareQueryParams(params ?? {}),
      }),
    }),
    getEnterpriseDashboardOrderCharts: builder.query<
      { data: ApiTypes.Res.EnterpriseDashboardOrderChartsWidget },
      EnterpriseDashboardWidgetQuery
    >({
      query: params => ({
        url: `/enterprises/dashboard/widgets/charts/orders`,
        method: "GET",
        params: prepareQueryParams(params),
      }),
    }),
    getEnterpriseDashboardAlertCharts: builder.query<
      { data: ApiTypes.Res.EnterpriseDashboardAlertChartsWidget },
      EnterpriseDashboardWidgetQuery
    >({
      query: params => ({
        url: `/enterprises/dashboard/widgets/charts/alerts`,
        method: "GET",
        params: prepareQueryParams(params),
      }),
    }),
    getEnterpriseDashboardPerftestCharts: builder.query<
      { data: ApiTypes.Res.EnterpriseDashboardPerftestChartsWidget },
      EnterpriseDashboardWidgetQuery
    >({
      query: params => ({
        url: `/enterprises/dashboard/widgets/charts/perftests`,
        method: "GET",
        params: prepareQueryParams(params),
      }),
    }),
  }),
})

export const {
  useLazyGetInventoryQuery,
  useLazyGetMonitoringQuery,
  useLazyGetStatusQuery,
  useLazyGetPerftestResultsQuery,
  useLazyGetPerftestStatusesQuery,
  useLazyGetAlertTypesQuery,
  useLazyGetAlertStatusesQuery,
  useLazyGetOrderProductsQuery,
  useLazyGetOrderStatusesQuery,
  useLazyGetInstalledDevicesQuery,
  useGetTotalStatsQuery,
  useLazyGetCustomersAppsTrendQuery,
  useLazyGetInstallationDevicesTrendQuery,
  useLazyGetSystemHealthTrendQuery,
  useGetEnterpriseDashboardTotalStatsQuery,
  useGetEnterpriseDashboardThermostatTrendQuery,
  useGetEnterpriseDashboardThermostatChartsQuery,
  useGetEnterpriseDashboardOrderStatusesQuery,
  useGetEnterpriseDashboardOrderChartsQuery,
  useGetEnterpriseDashboardAlertChartsQuery,
  useGetEnterpriseDashboardPerftestChartsQuery,
} = widgetsApi
